import { useState, useContext } from 'react';
import { Modal, ModalDialog, Typography, Button, Stack, Box, Sheet, Chip } from '@mui/joy';
import { GraduationCap, Barbell, Clock, Book } from "@phosphor-icons/react";
import { LayoutContext } from '../../context';
import { APISaveSurvey } from '../../api';

const TOPICS = [
    'Biochemistry',
    'Physiology',
    'Pharmacology',
    'Pathology',
    'Microbiology',
    'Anatomy',
];

const EXAM_OPTIONS = [
    { value: '1month', label: 'Через месяц' },
    { value: '3months', label: 'Через 3 месяца' },
    { value: '6months', label: 'Через 6 месяцев' },
    { value: '1year', label: 'Через год' },
    { value: 'just_learning', label: 'Просто учусь' },
];

const TIME_OPTIONS = [
    { value: 15, label: '15 мин' },
    { value: 30, label: '30 мин' },
    { value: 60, label: '1 час' },
    { value: 120, label: '2+ часа' },
];

const USMLE_OPTIONS = [
    { value: 'nothing', label: 'Ничего не знаю', emoji: '🤷' },
    { value: 'little', label: 'Знаю немного', emoji: '📖' },
    { value: 'experienced', label: 'Разбираюсь', emoji: '💪' },
];

const TOTAL_STEPS = 4;

// Опрос показывается на 3+ день использования (триггерится из App.js).
// 4 шага: дата экзамена → слабые темы → время в день → знания USMLE.
// Без welcome-приветствия и без редиректа на /plans — юзер уже в продукте.
export default function WelcomeModal({ open, onClose }) {
    const [step, setStep] = useState(0);
    const { token, updateUser } = useContext(LayoutContext);
    const [examDate, setExamDate] = useState(null);
    const [weakTopics, setWeakTopics] = useState([]);
    const [dailyMinutes, setDailyMinutes] = useState(null);
    const [usmleKnowledge, setUsmleKnowledge] = useState(null);
    const [saving, setSaving] = useState(false);

    const toggleTopic = (topic) => {
        setWeakTopics(prev =>
            prev.includes(topic)
                ? prev.filter(t => t !== topic)
                : prev.length < 3 ? [...prev, topic] : prev
        );
    };

    const saveSurveyAndClose = async (knowledge) => {
        if (saving) return;
        setSaving(true);
        try {
            await APISaveSurvey(token, {
                exam_date: examDate,
                weak_topics: weakTopics,
                daily_minutes: dailyMinutes,
                usmle_knowledge: knowledge,
            });
            updateUser({ survey_completed: true });
        } catch (e) {
            console.error('Survey save error:', e);
        }
        onClose();
        setSaving(false);
    };

    const handleSkip = async () => {
        setSaving(true);
        try {
            await APISaveSurvey(token, {});
            updateUser({ survey_completed: true });
        } catch (e) {
            console.error(e);
        }
        setSaving(false);
        onClose();
    };

    const canProceed = () => {
        if (step === 0) return !!examDate;
        if (step === 1) return weakTopics.length > 0;
        if (step === 2) return !!dailyMinutes;
        return true;
    };

    const renderStep = () => {
        switch (step) {
            // ═══ Step 0: Exam date ═══
            case 0:
                return (
                    <>
                        <Typography level="body-xs" color="neutral" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                            Настроим под тебя · 4 вопроса
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                            <GraduationCap size={48} color="#37352F" />
                        </Box>
                        <Typography level="h3" sx={{ mb: 1 }}>
                            Когда планируешь сдавать?
                        </Typography>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 2.5 }}>
                            Поможет рассчитать темп подготовки
                        </Typography>
                        <Stack spacing={1} sx={{ mb: 3, width: '100%' }}>
                            {EXAM_OPTIONS.map(opt => (
                                <Button
                                    key={opt.value}
                                    variant={examDate === opt.value ? 'solid' : 'outlined'}
                                    color={examDate === opt.value ? 'primary' : 'neutral'}
                                    onClick={() => setExamDate(opt.value)}
                                    sx={{
                                        justifyContent: 'center',
                                        fontWeight: examDate === opt.value ? 600 : 400,
                                        borderRadius: 'lg',
                                        py: 1.2,
                                    }}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </Stack>
                    </>
                );

            // ═══ Step 1: Weak topics ═══
            case 1:
                return (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Barbell size={48} color="#37352F" />
                        </Box>
                        <Typography level="h3" sx={{ mb: 1 }}>
                            Какие темы самые сложные?
                        </Typography>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 2.5 }}>
                            Выбери до 3 — начнём именно с них
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 3 }}>
                            {TOPICS.map(topic => {
                                const selected = weakTopics.includes(topic);
                                return (
                                    <Chip
                                        key={topic}
                                        variant={selected ? 'solid' : 'outlined'}
                                        color={selected ? 'primary' : 'neutral'}
                                        onClick={() => toggleTopic(topic)}
                                        sx={{
                                            cursor: 'pointer',
                                            px: 2,
                                            py: 0.8,
                                            fontSize: '0.9rem',
                                            fontWeight: selected ? 600 : 400,
                                            borderRadius: 'xl',
                                            transition: 'all 0.15s',
                                            ...(selected && {
                                                bgcolor: '#DA7756',
                                                '&:hover': { bgcolor: '#C4623E' },
                                            }),
                                        }}
                                    >
                                        {topic}
                                    </Chip>
                                );
                            })}
                        </Box>
                        {weakTopics.length > 0 && (
                            <Typography level="body-xs" color="neutral" sx={{ mb: 1 }}>
                                Выбрано: {weakTopics.length}/3
                            </Typography>
                        )}
                    </>
                );

            // ═══ Step 2: Daily time ═══
            case 2:
                return (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Clock size={48} color="#37352F" />
                        </Box>
                        <Typography level="h3" sx={{ mb: 1 }}>
                            Сколько времени в день?
                        </Typography>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 2.5 }}>
                            Подберём оптимальное количество вопросов
                        </Typography>
                        <Stack spacing={1} sx={{ mb: 3, width: '100%' }}>
                            {TIME_OPTIONS.map(opt => (
                                <Button
                                    key={opt.value}
                                    variant={dailyMinutes === opt.value ? 'solid' : 'outlined'}
                                    color={dailyMinutes === opt.value ? 'primary' : 'neutral'}
                                    onClick={() => setDailyMinutes(opt.value)}
                                    sx={{
                                        justifyContent: 'center',
                                        fontWeight: dailyMinutes === opt.value ? 600 : 400,
                                        borderRadius: 'lg',
                                        py: 1.2,
                                    }}
                                >
                                    {opt.label}
                                </Button>
                            ))}
                        </Stack>
                    </>
                );

            // ═══ Step 3: USMLE Knowledge ═══
            case 3:
                return (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <Book size={48} color="#37352F" />
                        </Box>
                        <Typography level="h3" sx={{ mb: 1 }}>
                            Что знаешь о USMLE?
                        </Typography>
                        <Typography level="body-sm" color="neutral" sx={{ mb: 2.5 }}>
                            Подберём материал под твой уровень
                        </Typography>
                        <Stack spacing={1.5} sx={{ mb: 3, width: '100%' }}>
                            {USMLE_OPTIONS.map(opt => (
                                <Sheet
                                    key={opt.value}
                                    variant="outlined"
                                    onClick={() => {
                                        if (saving) return;
                                        setUsmleKnowledge(opt.value);
                                        saveSurveyAndClose(opt.value);
                                    }}
                                    sx={{
                                        p: 2,
                                        borderRadius: 'lg',
                                        cursor: saving ? 'default' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        border: usmleKnowledge === opt.value ? '2px solid #DA7756' : '1.5px solid',
                                        borderColor: usmleKnowledge === opt.value ? '#DA7756' : 'neutral.300',
                                        bgcolor: usmleKnowledge === opt.value ? '#FFF8F0' : 'transparent',
                                        transition: 'all 0.15s',
                                        '&:hover': saving ? {} : {
                                            borderColor: '#DA7756',
                                            bgcolor: '#FFF8F0',
                                            transform: 'translateY(-1px)',
                                        },
                                    }}
                                >
                                    <Typography sx={{ fontSize: '1.5rem' }}>
                                        {opt.emoji}
                                    </Typography>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography level="title-sm" fontWeight={600}>
                                            {opt.label}
                                        </Typography>
                                        <Typography level="body-xs" color="neutral">
                                            {opt.value === 'nothing' && 'Проведём с нуля — от азов к клиническим задачам'}
                                            {opt.value === 'little' && 'Закроем пробелы и углубим теорию'}
                                            {opt.value === 'experienced' && 'Сразу к клиническим задачам и тестам'}
                                        </Typography>
                                    </Box>
                                </Sheet>
                            ))}
                        </Stack>
                        {saving && (
                            <Typography level="body-sm" color="neutral" sx={{ mt: 1 }}>
                                Сохраняем...
                            </Typography>
                        )}
                    </>
                );

            default:
                return null;
        }
    };

    return (
        <Modal open={open} onClose={handleSkip}>
            <ModalDialog
                sx={{
                    maxWidth: 440,
                    width: '90vw',
                    p: 4,
                    textAlign: 'center',
                    borderRadius: 'xl',
                }}
            >
                {renderStep()}

                {/* Step indicators */}
                <Stack direction="row" justifyContent="center" spacing={0.75} sx={{ mb: 3 }}>
                    {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
                        <Box
                            key={i}
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: i === step ? '#DA7756' : i < step ? '#DA775680' : 'neutral.300',
                                transition: 'background-color 0.2s',
                            }}
                        />
                    ))}
                </Stack>

                {/* Navigation — Steps 0-2 only (step 3 saves on click) */}
                {step < 3 && (
                    <Stack direction="row" spacing={1.5} justifyContent="center">
                        {step === 0 ? (
                            <>
                                <Button variant="plain" color="neutral" size="sm" onClick={handleSkip}>
                                    Пропустить
                                </Button>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={() => setStep(step + 1)}
                                    disabled={!canProceed()}
                                >
                                    Далее
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button variant="plain" color="neutral" size="sm" onClick={() => setStep(step - 1)}>
                                    Назад
                                </Button>
                                <Button
                                    variant="solid"
                                    color="primary"
                                    onClick={() => setStep(step + 1)}
                                    disabled={!canProceed()}
                                >
                                    Далее
                                </Button>
                            </>
                        )}
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    );
}
